// Seo.js
import React from 'react';
import { Helmet } from 'react-helmet-async';

const companyName = 'Housekaar';
const defaultDescriptionBase = `Housekaar Construction Company provides expert architectural design, interior design, and construction services in`;
const defaultKeywordsBase = `house construction, interior design, architectural design, construction company, Bihar, Housekaar`;
const companyLogoUrl = 'https://housekaar.com/static/media/housekaar_logo.0438311c1b4ca42e0f48.jpeg'; // Consider using an environment variable

const Seo = ({
  title,
  description,
  keywords,
  location,
  alternateLocations,
  imageUrl,
  article,
  publishedTime,
  modifiedTime,
}) => {
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const defaultTitle = `${companyName} - ${title} | Services in ${location}, Bihar`;
  const defaultDescription = `${defaultDescriptionBase} ${location}, Bihar. We specialize in creating beautiful and functional homes. Contact us for your dream home.`;
  const defaultKeywords = `${defaultKeywordsBase}, ${location}`;

  // Construct all keywords, including broader locations
  const allKeywords = Array.from(
    new Set([
      ...defaultKeywords.split(',').map(k => k.trim()),
      ...(alternateLocations ? alternateLocations.split(',').map(loc => loc.trim()) : []),
    ])
  ).join(', ');

  // Construct the final description, prioritizing the provided one
  const seoDescription = description || defaultDescription;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{defaultTitle}</title>
      <meta name="description" content={seoDescription} />
      <meta name="keywords" content={allKeywords} />
      <link rel="canonical" href={currentUrl} />
      <meta name="author" content={companyName} />
      <meta name="robots" content="index, follow" />

      {/* Open Graph / Facebook */}
      <meta property="og:title" content={defaultTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={companyName} />
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {article && publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {article && modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={defaultTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:url" content={currentUrl} />
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}

      {/* Location Specific Meta Tags */}
      <meta name="geo.region" content="IN-BR" />
      <meta name="geo.placename" content={location} />
      <meta name="geo.position" content="25.0961;85.3131" />
      <meta name="ICBM" content="25.0961, 85.3131" />

      {/* Structured Data (JSON-LD) - Website & Article */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': article ? 'Article' : 'WebSite',
          'name': defaultTitle,
          'description': seoDescription,
          'url': currentUrl,
          ...(imageUrl && { 'image': imageUrl }),
          'author': {
            '@type': 'Organization',
            'name': companyName,
          },
          ...(article && publishedTime && { 'datePublished': publishedTime }),
          ...(article && modifiedTime && { 'dateModified': modifiedTime }),
        })}
      </script>

      {/* Structured Data (JSON-LD) - Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": companyName,
          "url": currentUrl,
          "logo": companyLogoUrl,
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Your Street Address", // Replace with actual address
            "addressLocality": "Patna",
            "addressRegion": "Bihar",
            "postalCode": "Your Postal Code",   // Replace with actual postal code
            "addressCountry": "IN"
          },
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+918828531570",
            "contactType": "customer service"
          },
          "sameAs": [
            "https://www.facebook.com/share/1A7RjkoVih/",
            "https://x.com/Housekaar?t=bI_LEdCgjYrNZt-nZ8yJAg&s=08",
            "https://www.instagram.com/housekaar?utm_source=qr&igsh=emp2NjhiNmh3MTF4",
            // ... other social profiles ...
          ]
        })}
      </script>
    </Helmet>
  );
};

export default Seo;